export const VITARI_LANE_COUNT = 6;

export type VitariTile = {
  label: string;
  lane: number;
  row: number;
  column: number;
  surface: number;
  value: number | null; // null = no value (draw empty)
};

export type VitariPanel = {
  surface: number;
  rows: number;
  columns: number;
  tiles: VitariTile[];
};

export type VitariGrid = { panels: VitariPanel[]; unplaced: string[] };

/** Reads an `L<lane>R<row>C<column>S<surface>` tile label; null when it does not match. */
export function parseTileLabel(
  label: string
): { lane: number; row: number; column: number; surface: number } | null {
  const match = /^L(\d+)R(\d+)C(\d+)S(\d+)$/.exec(label);
  if (!match) return null;

  const [lane, row, column, surface] = match.slice(1).map(Number);
  if (lane < 1 || row < 1 || column < 1 || surface < 1) return null;

  return { lane, row, column, surface };
}

/**
 * Places each tile label on a schematic grid. Surfaces, rows and columns come only from the labels.
 * A label that does not parse, or whose lane is above VITARI_LANE_COUNT, is reported in `unplaced`.
 * A tile has `value: null` only when its index has no entry in `values`; a real 0 stays 0.
 */
export function buildVitariGrid(
  labels: string[],
  values: number[]
): VitariGrid {
  const panels = new Map<number, VitariPanel>();
  const unplaced: string[] = [];

  labels.forEach((label, index) => {
    const position = parseTileLabel(label);
    if (!position || position.lane > VITARI_LANE_COUNT) {
      unplaced.push(label);
      return;
    }

    const panel = panels.get(position.surface) ?? {
      surface: position.surface,
      rows: 0,
      columns: 0,
      tiles: [],
    };
    panel.rows = Math.max(panel.rows, position.row);
    panel.columns = Math.max(panel.columns, position.column);
    panel.tiles.push({
      label,
      ...position,
      value: index < values.length ? values[index] : null,
    });
    panels.set(position.surface, panel);
  });

  return {
    panels: Array.from(panels.values()).sort((a, b) => a.surface - b.surface),
    unplaced,
  };
}
